import React, { FC, useState, useEffect } from 'react';
import { Row, Typography, Switch, Tooltip, Badge, Button } from 'antd';
import { InfoCircleOutlined, UnorderedListOutlined, SettingOutlined } from '@ant-design/icons';
import { useNewIntegrationsStore } from '@/store/newIntegration';
import { useEdgesStore } from '@/store/edges';
import ListConfigModal from './ListConfigModal';

const { Text } = Typography;

interface ListModeIndicatorProps {
  sourceData: any;
}

const ListModeIndicator: FC<ListModeIndicatorProps> = ({ sourceData }) => {
  const [isListMode, setIsListMode] = useState(false);
  const [isListDetected, setIsListDetected] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const newIntegration = useNewIntegrationsStore(state => state.newIntegration);
  const setNewIntegration = useNewIntegrationsStore(state => state.setNewIntegration);
  const edges = useEdgesStore(state => state.edges);
  const setEdges = useEdgesStore(state => state.setEdges);

  // Detect if source data is a list
  useEffect(() => {
    const detectListPayload = () => {
      // Check if sourceData is an array or has array-like structure
      if (Array.isArray(sourceData)) {
        setIsListDetected(true);
        return;
      }

      // Check source variables if they might be a list
      if (newIntegration?.source?.variables) {
        const variables = newIntegration.source.variables as Record<string, any>;
        
        // Check if variables has an 'items' or 'data' property that's an array
        if (
          (variables.items && Array.isArray(variables.items)) ||
          (variables.data && Array.isArray(variables.data)) ||
          (variables.value && Array.isArray(variables.value)) ||
          (variables.results && Array.isArray(variables.results)) ||
          (variables.list && Array.isArray(variables.list))
        ) {
          setIsListDetected(true);
          return;
        }
      }

      // Check body content for array indicators
      if (newIntegration?.source?.body?.rawContent) {
        try {
          const bodyContent = JSON.parse(newIntegration.source.body.rawContent);
          if (Array.isArray(bodyContent)) {
            setIsListDetected(true);
            return;
          }
          
          // Check common list properties in APIs
          if (
            (bodyContent.items && Array.isArray(bodyContent.items)) || 
            (bodyContent.data && Array.isArray(bodyContent.data)) ||
            (bodyContent.results && Array.isArray(bodyContent.results)) ||
            (bodyContent.list && Array.isArray(bodyContent.list))
          ) {
            setIsListDetected(true);
            return;
          }
        } catch (e) {
          console.error("Failed to parse body content for list detection", e);
        }
      }

      setIsListDetected(false);
    };

    detectListPayload();
    
    if (newIntegration?.source?.isList) {
      setIsListMode(true);
    }
  }, [sourceData, newIntegration]);

  // Get the list path from source data
  const getListPath = (): string => {
    // First check if there's already a configured list path
    if (newIntegration?.source?.listPath) {
      return newIntegration.source.listPath;
    }
    
    if (Array.isArray(sourceData)) {
      return '';
    }
    
    const variables = newIntegration?.source?.variables as Record<string, any> | undefined;
    
    if (variables) {
      if (variables.items && Array.isArray(variables.items)) return 'items';
      if (variables.data && Array.isArray(variables.data)) return 'data';
      if (variables.value && Array.isArray(variables.value)) return 'value';
      if (variables.results && Array.isArray(variables.results)) return 'results';
      if (variables.list && Array.isArray(variables.list)) return 'list';
    }
    
    // Check body content
    if (newIntegration?.source?.body?.rawContent) {
      try {
        const bodyContent = JSON.parse(newIntegration.source.body.rawContent);
        if (bodyContent) {
          if (bodyContent.items && Array.isArray(bodyContent.items)) return 'items';
          if (bodyContent.data && Array.isArray(bodyContent.data)) return 'data';
          if (bodyContent.results && Array.isArray(bodyContent.results)) return 'results';
          if (bodyContent.list && Array.isArray(bodyContent.list)) return 'list';
        }
      } catch (e) {
        console.error("Failed to parse body content for list path detection", e);
      }
    }
    
    return '';
  };

  useEffect(() => {
    if (!isListDetected || !newIntegration || newIntegration.source?.isList != null) return
    const listPath = getListPath()
    setIsListMode(true)
    setNewIntegration({
      ...newIntegration,
      source: {
        ...newIntegration.source,
        isList: true,
        listPath: listPath || 'data',
      },
    })
    setEdges(edges.map(edge => ({
      ...edge,
      isList: true,
      listPath: listPath || 'data',
    })))
  }, [isListDetected])

  // Handle toggling of list mode
  const handleToggleListMode = (checked: boolean) => {
    setIsListMode(checked);
    
    // Update edges to include list iteration information
    const updatedEdges = edges.map(edge => ({
      ...edge,
      isList: checked,
      // Add list path handling - the path to iterate through in the source
      listPath: checked ? getListPath() : undefined
    }));
    
    setEdges(updatedEdges);
    
    // Store list mode in integration config
    if (newIntegration) {
      setNewIntegration({
        ...newIntegration,
        source: {
          ...newIntegration.source,
          isList: checked,
          // Store the path to the list in the source data
          listPath: checked ? getListPath() : undefined
        }
      });
    }
    
    // Open config modal if turning on list mode for the first time
    if (checked && !newIntegration?.source?.listConfig) {
      setConfigModalVisible(true);
    }
  };

  // If no list is detected, don't render anything
  if (!isListDetected) {
    return null;
  }

  return (
    <>
      <Row 
        style={{ 
          padding: '8px 16px', 
          backgroundColor: '#f0f7ff', 
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px',
          border: '1px solid #d6e8ff'
        }}
      >
        <Badge status="processing" color="#1664ff" />
        <Text style={{ marginLeft: '8px', marginRight: '8px' }}>
          <UnorderedListOutlined style={{ marginRight: '4px' }} />
          List data detected in source payload
        </Text>
        <Tooltip 
          title="Each object in the source list is mapped to its own destination message. Turn this off only if you want a single envelope for the whole payload."
        >
          <InfoCircleOutlined style={{ color: '#1664ff', marginRight: '12px' }} />
        </Tooltip>
        <Text style={{ marginRight: '12px' }}>Enable list processing:</Text>
        <Switch 
          checked={isListMode} 
          onChange={handleToggleListMode} 
          size="small"
        />
        {isListMode && (
          <Button 
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setConfigModalVisible(true)}
            style={{ marginLeft: '12px' }}
          >
            Configure List Processing
          </Button>
        )}
      </Row>
      
      <ListConfigModal 
        visible={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        listPath={getListPath()}
      />
    </>
  );
};

export default ListModeIndicator; 